import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VENUE_EVENTS } from '@/lib/shared-types';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useApi } from '@/Hooks/useApi';
import { useRealtime } from '@/Hooks/useRealtime';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/field';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { useCurrency } from '@/Hooks/useCurrency';
import {
  axisStyle,
  screenCurrency,
  seriesColors,
  useBranchOptions,
  useFormat,
  type DashboardResult,
} from '../Reports/analytics';
import { ChartTip, KpiTile } from '../Reports/components';
import SetupCard from '../Setup/SetupCard';

/** Realtime is a nudge to refetch, not a second source of truth - so coalesce it. */
const REFRESH_DEBOUNCE_MS = 1500;

/**
 * P13-06 - the live overview.
 *
 * Counters come from `GET /analytics/dashboard`, which reads current state for
 * anything live (a table is occupied *now*) and the §32 semantic layer for
 * anything cumulative (today's revenue, today by hour). Domain events (§27) only
 * trigger a refetch: deriving a tile from a socket payload would give the screen
 * its own arithmetic, and it would drift.
 */
export default function Overview() {
  const { t } = useTranslation();
  // Last resort only: every branch carries a currency, and useCurrency resolves
  // branch › tenant before this is ever reached.
  const { code: fallbackCurrency } = useCurrency();
  const { branches } = useBranchOptions();
  const [branchId, setBranchId] = useState('');

  // D-02 — with "All branches" this used to fall back to `branches[0].currency`,
  // stamping a tenant-wide sum with whichever branch happened to sort first.
  // `screenCurrency` returns null instead when the chain spans several, and the
  // banner below says so rather than printing a confident wrong label.
  const { code: currency, mixed, codes } = screenCurrency(branches, branchId, fallbackCurrency);
  const format = useFormat(currency);

  const { data, loading, error, reload } = useApi<DashboardResult>('/analytics/dashboard', {
    ...(branchId ? { branchId } : {}),
  });

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(reload, REFRESH_DEBOUNCE_MS);
  }, [reload]);

  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);

  useRealtime(VENUE_EVENTS.TABLE_STATUS_CHANGED, refresh);
  useRealtime(VENUE_EVENTS.ORDER_CREATED, refresh);
  useRealtime(VENUE_EVENTS.ORDER_COMPLETED, refresh);
  useRealtime(VENUE_EVENTS.ITEM_READY, refresh);
  useRealtime(VENUE_EVENTS.PAYMENT_COMPLETED, refresh);
  useRealtime(VENUE_EVENTS.SERVICE_REQUEST_UPDATED, refresh);
  useRealtime(VENUE_EVENTS.RESOURCE_SESSION_STARTED, refresh);
  useRealtime(VENUE_EVENTS.RESOURCE_SESSION_ENDED, refresh);

  const live = data?.live;
  const kitchen = data?.kitchen;
  const today = data?.today ?? {};
  const hourly = (data?.hourly ?? []).map((row) => ({
    ...row,
    revenueValue: Number(row.revenue ?? 0),
  }));

  return (
    <div className="space-y-6">
      {/* C-15 — renders itself only while the venue still has a gap this actor
          can close, so it disappears for good once setup is finished. */}
      <SetupCard />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('nav.overview')}</h1>
        <div className="w-56">
          <Select
            className="h-9"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            aria-label={t('nav.branches')}
          >
            <option value="">{t('reports.filters.allBranches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {mixed && (
        <p
          role="status"
          className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {t('reports.currency.mixedNotice', {
            codes: codes.join(', '),
          })}
        </p>
      )}

      {error ? (
        <ErrorState
          message={t('reports.errors.load')}
          onRetry={reload}
        />
      ) : loading && !data ? (
        <LoadingState label={t('common.loading')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              label={t('overview.openTables')}
              value={format.number(live?.openTables ?? 0)}
            />
            <KpiTile
              label={t('overview.activeOrders')}
              value={format.number(live?.activeOrders ?? 0)}
              hint={t('kds.board.itemCount', { count: kitchen?.openItems ?? 0 })}
            />
            <KpiTile
              label={t('overview.runningTimers')}
              value={format.number(live?.runningTimers ?? 0)}
            />
            <KpiTile
              label={t('overview.todayRevenue')}
              value={format.money(today.revenue ?? null)}
              hint={t('waiter.actions.orders', { count: Number(today.orders ?? 0) })}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t('overview.todayByHour')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={hourly} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                      <defs>
                        <linearGradient id="overviewFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={seriesColors[0]} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={seriesColors[0]} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        stroke={axisStyle.grid}
                        strokeDasharray="3 3"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="hour"
                        tick={axisStyle.tick}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => format.hour(value as number)}
                      />
                      <YAxis
                        tick={axisStyle.tick}
                        tickLine={false}
                        axisLine={false}
                        width={64}
                        tickFormatter={(value) => format.number(value as number)}
                      />
                      <Tooltip
                        cursor={{ stroke: axisStyle.grid }}
                        content={
                          <ChartTip
                            formatValue={(_key, value) => format.money(value ?? null)}
                            formatLabel={(label) => format.hour(label as number)}
                          />
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="revenueValue"
                        name={t('reports.kpi.revenue')}
                        stroke={seriesColors[0]}
                        strokeWidth={2}
                        fill="url(#overviewFill)"
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('overview.serviceLoad')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Row
                  label={t('overview.openRequests')}
                  value={format.number(live?.openServiceRequests ?? 0)}
                />
                <Row
                  label={t('overview.awaitingPickup')}
                  value={format.number(kitchen?.awaitingPickup ?? 0)}
                />
                <Row
                  label={t('overview.slaBreached')}
                  value={format.number(kitchen?.slaBreached ?? 0)}
                  tone={kitchen?.slaBreached ? 'danger' : 'success'}
                />
                <Row
                  label={t('reports.kpi.prep')}
                  value={format.duration(today.prepSeconds ?? null)}
                />
                <Row
                  label={t('reports.kpi.waiterResponse')}
                  value={format.duration(today.waiterResponseSeconds ?? null)}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'danger' | 'success';
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      {tone ? (
        <Badge tone={tone}>{value}</Badge>
      ) : (
        <span className="font-medium tabular-nums">{value}</span>
      )}
    </div>
  );
}
